import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Mark } from "./Mark";
import { NAV_LINKS } from "../data/content";

export const Nav = () => {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const onHome = useLocation().pathname === "/";

  const Anchor = ({ href, children, ...rest }) =>
    onHome ? (
      <a href={href} {...rest}>
        {children}
      </a>
    ) : (
      <Link to={`/${href}`} {...rest}>
        {children}
      </Link>
    );

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 70);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="nav-container"
      initial={{ y: -90 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        solid ? "glass-nav border-b border-navy/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <Mark className="h-11 w-11" onDark={!solid} />
          <span className="leading-none">
            <span
              className={`font-display block text-[0.98rem] tracking-[0.13em] transition-colors duration-500 ${
                solid ? "text-navy" : "text-sand"
              }`}
            >
              THREE ABIDE
            </span>
            <span
              className={`label-eyebrow block text-[0.53rem] transition-colors duration-500 ${
                solid ? "text-sage" : "text-gold"
              }`}
            >
              Ministries
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-9 md:flex">
          {NAV_LINKS.map((l) => (
            <Anchor
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`link-wipe font-mono-accent text-[0.7rem] uppercase tracking-[0.2em] transition-colors duration-500 ${
                solid ? "text-navy/70 hover:text-navy" : "text-sand/75 hover:text-sand"
              }`}
            >
              {l.label}
            </Anchor>
          ))}
          <Anchor
            href="#give"
            data-testid="nav-give-now-btn"
            className="btn-sheen rounded-full bg-crimson px-6 py-2.5 font-mono-accent text-[0.68rem] uppercase tracking-[0.2em] text-sand"
          >
            Give Now
          </Anchor>
        </nav>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden ${solid ? "text-navy" : "text-sand"}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-navy/10 bg-sand/95 md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {[...NAV_LINKS, { label: "Give Now", href: "#give" }].map((l) => (
                <Anchor
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display py-2.5 text-xl text-navy"
                >
                  {l.label}
                </Anchor>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
